import { ParsedUrlQuery } from 'querystring'
import { Box, Stack, SxProps } from '@mui/material'
import MuiTab, { TabProps as MuiTabProps } from '@mui/material/Tab'
import MuiTabs, { TabsProps as MuiTabsProps } from '@mui/material/Tabs'
import { Features } from 'c1gen/features'
import { C1CountChip } from 'components/core/count/C1CountChip'
import { useUnsafeFeatureChecker, useFeatureCheckerLazy } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { useParams, useRouter } from 'next/navigation'
import React, { ReactNode, useState, useRef, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

// https://github.com/mui-org/material-ui/issues/17454
type TabsProps = Omit<MuiTabsProps, 'onChange'> & {
    onChange: (event: React.SyntheticEvent, newValue: number) => void
}

type TabProps = MuiTabProps

type TabPanelProps = {
    children?: ReactNode
    index: number
    value: number
    padded?: boolean
    id?: string
    'aria-labelledby'?: string
}

type TabLabelProps = {
    label: ReactNode
    count?: number
}

export const Tabs = ({ children, 'aria-label': ariaLabel, ...props }: TabsProps): React.ReactElement => {
    const intl = useIntl()
    const ariaLabelText = ariaLabel || intl.formatMessage({ id: 'components_tabs_aria_label', defaultMessage: 'tabs' })

    // Auto-inject id and aria-controls into Tab children if not provided
    const enhancedChildren = React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
            const tabId = (child.props as TabProps).id || `tab-${index}`
            const tabPanelId = (child.props as TabProps)['aria-controls'] || `tabpanel-${index}`

            return React.cloneElement(child as React.ReactElement<TabProps>, {
                id: tabId,
                'aria-controls': tabPanelId,
            })
        }
        return child
    })

    return <MuiTabs
        indicatorColor="primary"
        selectionFollowsFocus={false}
        aria-label={ariaLabelText}
        {...props}
        TabIndicatorProps={{ children: <span /> }}
    >
        {enhancedChildren}
    </MuiTabs>
}

export const TabLabel = ({ label, count }: TabLabelProps): React.ReactElement => (
    <Stack
        direction="row"
        spacing={1}
        sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        }}
    >
        <Box>{label}</Box>
        <C1CountChip label={count} />
    </Stack>
)

export const Tab = ({ ...props }: TabProps): React.ReactElement => <MuiTab
    disableRipple
    sx={{
        '&.MuiTab-textColorInherit': {
            opacity: 1,
        },
    }}
    {...props}
/>

export const TabPanel = ({
    children,
    value,
    index,
    padded = true,
    id, 'aria-labelledby': ariaLabelledby,
    ...props
}: TabPanelProps): React.ReactElement => (
    <div
        className="TabPanel-root"
        role="tabpanel"
        hidden={value !== index}
        id={id || `tabpanel-${index}`}
        aria-labelledby={ariaLabelledby || `tab-${index}`}
        {...props}
    >
        {value === index && <Box sx={{ padding: 0, ...(padded && { pt: 3 }) }}>{children}</Box>}
    </div>
)

type HasFeature = {
    feature?: Features
}

export const filterFeature = (t: HasFeature, featureChecker: (feature: Features) => boolean): boolean => {
    if (!t.feature) {
        return true
    }
    return featureChecker(t.feature)
}

export type TabWithFeature = {
    key: string
    title: string | React.ReactElement
    tab: React.ReactElement
    feature?: Features
    icon?: string | React.ReactElement
    disabled?: boolean
}

export const SmartTabsWithRoutingContext = React.createContext<{
    tab: string | string[] | undefined
}>({
    tab: undefined,
})

type SmartTabsWithRoutingProps = {
    menuKey: string
    tabs: TabWithFeature[]
    onTabChange: (newValue: string) => string
    tabFromRouterQuery: (query: ParsedUrlQuery) => string | string[] | undefined
    defaultTabKey?: string
    isOnGreyBackground?: boolean
    px?: number
    sx?: SxProps
} & Pick<TabPanelProps, 'padded'>

export const SmartTabsWithRouting = ({
    menuKey,
    tabs,
    onTabChange,
    tabFromRouterQuery,
    defaultTabKey,
    isOnGreyBackground = false,
    ...rest
}: SmartTabsWithRoutingProps): React.ReactElement => {
    const router = useRouter()
    const featureChecker = useUnsafeFeatureChecker()
    const params = useParams()

    const tabNumFromKey = (key: string): number => {
        return tabs
            .filter((f) => {
                if (f.feature !== undefined) {
                    return featureChecker(f.feature)
                }

                return true
            })
            .map((t) => t.key)
            .indexOf(key)
    }

    let defaultTab: number
    const tabKey = tabFromRouterQuery(params)

    if (
        !Array.isArray(tabKey) ||
        tabKey.length === 0 ||
        tabs.map((t) => t.key).find((k) => k === tabKey[0]) === undefined
    ) {
        defaultTab = tabNumFromKey(defaultTabKey || (tabs.length ? tabs[0].key : ''))
    } else {
        defaultTab = tabNumFromKey(tabKey[0])
    }

    const tabKeyFromNum = (num: number): string => {
        return tabs
            .filter((f) => {
                if (f.feature !== undefined) {
                    return featureChecker(f.feature)
                }

                return true
            })
            .map((t) => t.key)[num]
    }

    const tabChangeHandler = async (_: React.SyntheticEvent, newValue: number): Promise<void> => {
        const newRoute = onTabChange(tabKeyFromNum(newValue))
        router.replace(newRoute)
    }

    const tabContext = useMemo(() => ({ tab: tabKey }), [tabKey])

    return <SmartTabsWithRoutingContext.Provider value={tabContext}>
        <TabsWithBody
            menuKey={menuKey}
            tabs={tabs}
            onTabChange={tabChangeHandler}
            activeTab={defaultTab}
            isOnGreyBackground={isOnGreyBackground}
            {...rest}
        />
    </SmartTabsWithRoutingContext.Provider>
}

type SmartTabProps = {
    menuKey: string
    tabs: TabWithFeature[]
    defaultTab: string
    disableScrollIntoViewBehavior?: boolean
} & Pick<TabPanelProps, 'padded'>

export const SmartTabsContext = React.createContext<{
    tab: string | null
    setTab: React.Dispatch<React.SetStateAction<string>>
}>({
    tab: '',
    setTab: (): void => undefined,
})

export const SmartTabs = ({ menuKey, tabs, defaultTab, padded, disableScrollIntoViewBehavior }: SmartTabProps): React.ReactElement => {
    const featureChecker = useFeatureCheckerLazy()
    const [tab, setTab] = useState<string>(defaultTab)
    const tabRef = useRef<null | HTMLDivElement>(null)

    useEffect((): void => {
        if (!disableScrollIntoViewBehavior) {
            // attempt to get the container element, otherwise default to current
            const container = (tabRef && tabRef.current && tabRef.current?.closest('.MuiContainer-root')) || tabRef.current
            if (container) {
                container.scrollIntoView({ behavior: 'smooth' })
            }
        }
    }, [tab, disableScrollIntoViewBehavior])

    const tabNumFromKey = (key: string): number => {
        return tabs
            .filter((f) => {
                if (f.feature !== undefined) {
                    return featureChecker(f.feature)
                }

                return true
            })
            .map((t) => t.key)
            .indexOf(key)
    }

    const tabKeyFromNum = (num: number): string => {
        return tabs
            .filter((f) => {
                if (f.feature !== undefined) {
                    return featureChecker(f.feature)
                }

                return true
            })
            .map((t) => t.key)[num]
    }

    const tabChangeHandler = async (event: React.SyntheticEvent, newValue: number): Promise<void> => {
        setTab(tabKeyFromNum(newValue))
    }

    // useMemo for tab and setTab
    const tabContext = useMemo(() => ({ tab, setTab }), [tab, setTab])

    if (!tab) {
        return <></>
    }

    return <SmartTabsContext.Provider value={tabContext}>
        <Box ref={tabRef}>
            <TabsWithBody
                menuKey={menuKey}
                tabs={tabs}
                onTabChange={tabChangeHandler}
                activeTab={tabNumFromKey(tab)}
                padded={padded}
            />
        </Box>
    </SmartTabsContext.Provider>
}

type TabsWithBodyProps = {
    menuKey: string
    tabs: TabWithFeature[]
    onTabChange: (event: React.SyntheticEvent, newValue: number) => void
    activeTab: number
    isOnGreyBackground?: boolean
    px?: number
    sx?: SxProps
} & Pick<TabPanelProps, 'padded'>

export const TabsWithBody = ({
    menuKey, tabs, onTabChange, activeTab, isOnGreyBackground = false, px = 0, padded, sx }: TabsWithBodyProps): React.ReactElement => {
    const featureChecker = useUnsafeFeatureChecker()
    const filteredTabs = tabs.filter((t) => filterFeature(t, featureChecker))

    return <>
        <Tabs
            onChange={onTabChange}
            value={activeTab}
            aria-label={`${menuKey} tabs`}
            sx={sxc([isOnGreyBackground, { backgroundColor: 'background.default' }], [px !== undefined, { px }], sx)}
        >
            {filteredTabs.map((t, idx) => {
                return <Tab
                    key={`${menuKey}-tab-${idx}`}
                    id={`${menuKey}-tab-${idx}`}
                    aria-controls={`${menuKey}-tabpanel-${idx}`}
                    label={t.title}
                    icon={t.icon}
                    iconPosition="end"
                    disabled={t.disabled}
                    data-testid={`${menuKey}-tab-${t.key}`}
                />
            })}
        </Tabs>
        {filteredTabs.map((t, idx) => {
            return <TabPanel
                value={activeTab}
                index={idx}
                key={`${menuKey}-tab-panel-${idx}`}
                id={`${menuKey}-tabpanel-${idx}`}
                aria-labelledby={`${menuKey}-tab-${idx}`}
                padded={padded}
            >
                {t.tab}
            </TabPanel>
        })}
    </>
}
